import { createAsyncThunk } from '@reduxjs/toolkit'

import questionsRequest from 'src/api/requests/questions'
import questionsActions from 'src/store/domain/automaticPlan/questions'
import menuActions from 'src/store/domain/automaticPlan/menus'
import history from 'src/libs/history'
import uiActions from 'src/store/ui/notification'
import backFlowActions from 'src/store/domain/automaticPlan/backFlow'
import { PATH as AUTOMATIC_PLAN_PATH } from 'src/pages/automaticPlan'
import { UserInterface } from 'src/interfaces/user'
import { defaultUserInfo } from './../interfaces/user.d'
import { get } from 'lodash'
import LocalStorage from 'src/utils/LocalStorage'
import { PATH as VIDEO_PATH } from 'src/pages/video'
import { USER_TREATMENT_STATE } from 'src/enum/userTreatmentState'
import { PATH as PATH_CHAT } from 'src/pages/chat/Chat'
import { PATH as PATH_REGULAR } from 'src/pages/regular'
import appActions from 'src/store/ui/app'

export interface QuestionRequestInterface {
  menu_id: string
}

export interface MenuRequestType {
  selected?: boolean
  allow_menu_ids?: Array<string | number>
}

export const getMenus = createAsyncThunk<any, MenuRequestType>(
  'get_menus',
  async (params, { dispatch }) => {
    try {
      dispatch(menuActions.updateLoading(true))

      const res = await questionsRequest.getMenus(params)
      if (res.data) {
        dispatch(menuActions.updateEntity(res.data.data))
      }
      dispatch(menuActions.updateLoading(false))
    } catch (error) {
      dispatch(menuActions.updateLoading(false))
    }
  },
)

export const getMenusByLp = createAsyncThunk(
  'automaticPlan/getMenusByLp',
  async (
    { clinicId, params }: { clinicId: string; params?: Record<string, any> },
    { dispatch },
  ) => {
    try {
      dispatch(menuActions.updateLoading(true))
      const res = await questionsRequest.getMenusByLp(clinicId, params || {})
      if (res.data) {
        dispatch(menuActions.updateEntity(res.data.data))
      }
      return res.data
    } finally {
      dispatch(menuActions.updateLoading(false))
    }
  },
)

export const getQuestionsByMenuId = createAsyncThunk<
  any,
  QuestionRequestInterface
>('get_questions_by_menu_id', async ({ menu_id }, { dispatch }) => {
  try {
    dispatch(questionsActions.updateLoading(true))
    dispatch(questionsActions.updateEntity([]))
    const res = await questionsRequest.getQuestionsByMenuId(menu_id)
    if (res.data) {
      dispatch(questionsActions.updateEntity(res.data))
    }
    dispatch(questionsActions.updateLoading(false))
  } catch (error) {
    dispatch(questionsActions.updateLoading(false))
  }
})

export const getQuestions = createAsyncThunk<any, QuestionRequestInterface>(
  'get_questions',
  async ({ menu_id }, { dispatch }) => {
    try {
      dispatch(questionsActions.updateLoading(true))
      const res = await questionsRequest.getQuestions(menu_id)
      if (res.data) {
        dispatch(questionsActions.updateEntity(res.data))
      }
      dispatch(questionsActions.updateLoading(false))
    } catch (error) {
      dispatch(questionsActions.updateLoading(false))
    }
  },
)

export const getMenuById = createAsyncThunk(
  'automaticPlan/getMenuById',
  async (
    {
      menuId,
      params,
    }: {
      menuId: string | number
      params?: Record<string, any>
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(menuActions.updateLoading(true))
      const state = getState() as any
      const user = get(state, 'ui.app.authInfo')
      const queryParams = params ? { ...params } : {}

      if (!queryParams.clinic_id && user?.clinics?.[0]?.id) {
        queryParams.clinic_id = user.clinics[0].id
      }

      const res = await questionsRequest.getMenuById(menuId, queryParams)
      const data = get(res, 'data.data')

      if (data) {
        const cleanedData = {
          ...data,
          plans: data.plans || [],
        }
        dispatch(menuActions.updateEntity([cleanedData]))
      }

      return data
    } finally {
      dispatch(menuActions.updateLoading(false))
    }
  },
)

export interface PostAnswer {
  answer: any
  question_id: number
  question_type: string
}
export interface PostAnswersInterView {
  answers: PostAnswer[]
  reservation_id: string
  user_treatment_id?: string | number
  menu_id?: string | number
  from?: string
  params?: any
}

export const answerInterView = createAsyncThunk<any, PostAnswersInterView>(
  'answer_interview',
  async (
    { answers, reservation_id, user_treatment_id, menu_id, from, params },
    { dispatch, getState },
  ) => {
    try {
      dispatch(questionsActions.updateLoading(true))
      const res = await questionsRequest.answer({
        answers,
        reservation_id,
        user_treatment_id,
      })
      const state = getState()
      const user: UserInterface = get(state, 'ui.app.authInfo', defaultUserInfo)
      const treatmentTele = res?.data?.user_treatment?.telemedicine_enabled
      const isTelemedicine =
        treatmentTele == 2 ||
        (treatmentTele != 1 && !!user?.clinics?.[0]?.telemedicine_enabled)
      const userTreatmentState = res?.data?.user_treatment.state

      // [SCOPE] Refresh the cached user_treatment_id right after answering so
      // screens reading LocalStorage.getUserTreatmentId() (e.g. Video/Schedule)
      // use the treatment just answered instead of a stale one left over from
      // a previous menu/session — affects any flow relying on this global key.
      // Pre-existing behavior: id was never rewritten here, so an older cached
      // id could leak into the next doctor-shift/video-schedule call.
      if (res?.data?.user_treatment?.id) {
        LocalStorage.setUserTreatmentId(res.data.user_treatment.id)
      }

      // Update the treatment state in authInfo to avoid stale state in usePlanSelection
      if (res.data?.user_treatment && user?.user_treatments) {
        const updatedUserTreatments = user.user_treatments.map((ut: any) =>
          ut.id === res.data.user_treatment.id
            ? { ...ut, state: res.data.user_treatment.state }
            : ut,
        )
        dispatch(
          appActions.patchAuthInfo({ user_treatments: updatedUserTreatments }),
        )
      }

      if (!user?.otp_verified_at && !isTelemedicine) {
        dispatch(
          backFlowActions.setBackFlow({
            from: from ?? null,
            params: params ?? null,
          }),
        )
        return history.push(
          `${AUTOMATIC_PLAN_PATH.PHONE_VERIFICATION}?menu_id=${menu_id}`,
        )
      } else if (isTelemedicine) {
        // Pass menu_id so the schedule form can forward it to SMS auth,
        // allowing verifyOTP to identify the correct treatment directly.
        history.push(
          menu_id
            ? `${VIDEO_PATH.VIDEO_SCHEDULE}?menu_id=${menu_id}`
            : VIDEO_PATH.VIDEO_SCHEDULE,
        )
        LocalStorage.removeTelemedicine()
      } else {
        switch (userTreatmentState) {
          case USER_TREATMENT_STATE.AWAITING_PLAN:
            return history.push(
              `${AUTOMATIC_PLAN_PATH.FINAL_QUESTIONS.replace(
                ':id',
                String(menu_id || ''),
              )}`,
            )

          case USER_TREATMENT_STATE.CHAT_INTERVIEW_SCHEDULED:
          case USER_TREATMENT_STATE.CHAT_CONSULTATION_IN_PROGRESS:
            return history.push(PATH_CHAT)

          default:
            history.push(
              `${PATH_REGULAR.REGULAR_SUBSCRIPTION}?menu_id=${menu_id}`,
            )
            break
        }
      }

      if (res.data) {
        dispatch(questionsActions.updateEntity(res.data?.questions))
      }
      dispatch(questionsActions.updateLoading(false))
    } catch (error: any) {
      const message = error?.response?.data.message
      dispatch(questionsActions.updateLoading(false))
      dispatch(
        uiActions.onNotification({
          type: 'error',
          message: message,
        }),
      )
    }
  },
)

export const draffAnswer = createAsyncThunk<any, PostAnswersInterView>(
  'draff_answer',
  async ({ answers, reservation_id }, { dispatch }) => {
    try {
      dispatch(questionsActions.updateDraffLoading(true))
      await questionsRequest.draffAnswer({
        answers,
        reservation_id,
      })
      dispatch(questionsActions.updateDraffLoading(false))
    } catch (error) {
      dispatch(questionsActions.updateDraffLoading(false))
    }
  },
)
